import { DEPARTMENT_LIST } from "@listou/shared/departments";
import {
  NAME_FIRST_YEAR,
  NAME_LAST_YEAR,
  namePath,
  DEPARTMENTS_BY_CODE,
  type RankedName,
  type YearDepartmentPage as YearDepartmentData,
  type YearPage as YearData,
  type YearsOverview,
} from "@listou/shared/schemas/prenoms";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";

import { nameCards } from "../../lib/api";
import { FranceMap, TimeChart, Legend } from "./charts";
import {
  BOYS,
  GIRLS,
  GRID,
  OTHER_COLOR,
  number,
  ordinal,
  percent,
  reignsOf,
  sum,
  type Reign,
} from "./format";
import {
  Breadcrumbs,
  card,
  DepartmentLink,
  Faq,
  NameLink,
  NamesLayout,
  pill,
  PrenomsNav,
  Source,
  YearDepartmentLink,
  YearLink,
} from "./layout";
import { colorsFor, LeaderLegend, RankingColumns, Reigns, tally } from "./ranking";
import { ShareButton } from "./share";
import {
  breadcrumbJsonLd,
  datasetJsonLd,
  faqJsonLd,
  graph,
  rankingJsonLd,
} from "./structured-data";

const topShare = (entries: RankedName[], births: number, size = 10) =>
  births > 0 ? (sum(entries.slice(0, size).map((entry) => entry.count)) / births) * 100 : 0;
const list = (names: string[]) =>
  names.length < 2 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} et ${names.at(-1) ?? ""}`;
const biggestClimb = (entries: RankedName[]) =>
  entries
    .filter((entry) => entry.previousRank !== null)
    .map((entry) => ({ entry, gain: (entry.previousRank ?? 0) - entry.rank }))
    .toSorted((a, b) => b.gain - a.gain)[0];

const nationalNote = (entry: RankedName | undefined) =>
  entry && entry.nationalRank !== null && entry.nationalRank !== 1
    ? `, ${ordinal(entry.nationalRank)} au niveau national`
    : entry?.nationalRank === 1
      ? ", comme dans toute la France"
      : ", hors du top 100 national";
const longest = (reigns: Reign[]) => reigns.toSorted((a, b) => b.to - b.from - (a.to - a.from))[0];
const totalBirths = (entry: YearsOverview[number]) => entry.births.girls + entry.births.boys;

const yearsAround = (year: number) => ({
  previous: year > NAME_FIRST_YEAR ? year - 1 : null,
  next: year < NAME_LAST_YEAR ? year + 1 : null,
});

function YearStepper({ year }: { year: number }) {
  const { previous, next } = yearsAround(year);
  return (
    <div className="flex flex-wrap gap-2">
      {previous && (
        <YearLink year={previous} className={pill}>
          <ChevronLeft size={16} aria-hidden="true" />
          {previous}
        </YearLink>
      )}
      {next && (
        <YearLink year={next} className={pill}>
          {next}
          <ChevronRight size={16} aria-hidden="true" />
        </YearLink>
      )}
    </div>
  );
}

/** Every year as a link, grouped by decade: crawl paths and a quick way to jump around. */
export function DecadeGrid({
  title,
  link,
  current,
}: {
  title: string;
  link: (year: number) => React.ReactNode;
  current?: number;
}) {
  const decades = Array.from(
    { length: Math.floor(NAME_LAST_YEAR / 10) - Math.floor(NAME_FIRST_YEAR / 10) + 1 },
    (_, index) => NAME_FIRST_YEAR + index * 10,
  );
  return (
    <section className={`${card} space-y-4`}>
      <h2 className="text-2xl font-bold">{title}</h2>
      <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
        {decades.map((start) => (
          <div key={start}>
            <p className="mb-1 text-sm font-bold text-muted">Années {start}</p>
            <p className="flex flex-wrap gap-x-3 gap-y-1">
              {Array.from({ length: 10 }, (_, i) => start + i)
                .filter((year) => year >= NAME_FIRST_YEAR && year <= NAME_LAST_YEAR)
                .map((year) => (
                  <span key={year} className={year === current ? "font-bold" : "underline"}>
                    {link(year)}
                  </span>
                ))}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function DepartmentGrid({
  title,
  link,
  current,
}: {
  title: string;
  link: (slug: string, name: string) => React.ReactNode;
  current?: string;
}) {
  return (
    <section className={`${card} space-y-4`}>
      <h2 className="text-2xl font-bold">{title}</h2>
      <ul className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2 lg:grid-cols-4">
        {DEPARTMENT_LIST.map((department) => (
          <li key={department.code} className={department.code === current ? "font-bold" : ""}>
            {link(department.slug, `${department.name} (${department.code})`)}
          </li>
        ))}
      </ul>
    </section>
  );
}

const faqFor = (data: YearData) => {
  const girls = data.girls.slice(0, 5).map((entry) => entry.name);
  const boys = data.boys.slice(0, 5).map((entry) => entry.name);
  return [
    {
      question: `Quel est le prénom le plus donné en France en ${data.year} ?`,
      answer: `${data.girls[0]?.name ?? "Aucun prénom"} chez les filles (${number(data.girls[0]?.count ?? 0)} naissances) et ${data.boys[0]?.name ?? "aucun prénom"} chez les garçons (${number(data.boys[0]?.count ?? 0)} naissances), selon le fichier des prénoms de l’Insee.`,
    },
    {
      question: `Quels sont les prénoms de fille les plus populaires en ${data.year} ?`,
      answer: `Le top 5 des prénoms de fille en ${data.year} : ${list(girls)}.`,
    },
    {
      question: `Quels sont les prénoms de garçon les plus populaires en ${data.year} ?`,
      answer: `Le top 5 des prénoms de garçon en ${data.year} : ${list(boys)}.`,
    },
    {
      question: `D’où viennent ces classements ?`,
      answer: `Ils reposent sur le fichier des prénoms de l’Insee, qui recense le premier prénom déclaré à l’état civil pour les naissances en France. Les effectifs sont arrondis à 5 et les prénoms rares sont masqués.`,
    },
  ];
};

export const yearHead = (data: YearData) => {
  const girl = data.girls[0];
  const boy = data.boys[0];
  return {
    title: `Top prénoms ${data.year} : filles et garçons les plus donnés · Listou`,
    image: nameCards.year(data.year, "og"),
    description: `Les prénoms les plus donnés en France en ${data.year} : ${girl?.name ?? "—"} chez les filles, ${boy?.name ?? "—"} chez les garçons. Classement complet, évolution et carte par département, selon l’Insee.`,
    jsonLd: graph(
      breadcrumbJsonLd(
        [
          { label: "Prénoms", to: "/prenoms" },
          { label: "Par année", to: "/prenoms/annee" },
          { label: String(data.year) },
        ],
        namePath.year(data.year),
      ),
      rankingJsonLd(`Prénoms de fille les plus donnés en ${data.year}`, data.girls.slice(0, 20)),
      rankingJsonLd(`Prénoms de garçon les plus donnés en ${data.year}`, data.boys.slice(0, 20)),
      faqJsonLd(faqFor(data)),
      datasetJsonLd({
        name: `Prénoms donnés en France en ${data.year}`,
        description: `Classement des prénoms des enfants nés en France en ${data.year}, filles et garçons.`,
        coverage: String(data.year),
      }),
    ),
  };
};

function LeaderCard({
  entry,
  sex,
  color,
  year,
}: {
  entry: RankedName | undefined;
  sex: string;
  color: string;
  year: number;
}) {
  if (!entry) return null;
  return (
    <div className="rounded-3xl p-6 text-white" style={{ background: color }}>
      <p className="text-sm opacity-90">
        N° 1 chez les {sex} en {year}
      </p>
      <NameLink
        slug={entry.slug}
        className="mt-1 block font-display text-5xl font-bold sm:text-6xl"
      >
        {entry.name}
      </NameLink>
      <p className="mt-2 opacity-90">{number(entry.count)} naissances</p>
    </div>
  );
}

export function YearPage({ data }: { data: YearData }) {
  const [sex, setSex] = useState<"girls" | "boys">("girls");
  const girl = data.girls[0];
  const boy = data.boys[0];
  const births = data.births.girls + data.births.boys;
  const leaders = data.leaders.map((entry) => (sex === "girls" ? entry.girl : entry.boy));
  const counts = tally(leaders);
  const colors = colorsFor([...counts.keys()]);
  const byCode = new Map(
    data.leaders.map((entry) => [entry.code, sex === "girls" ? entry.girl : entry.boy]),
  );
  const climbGirls = biggestClimb(data.girls.slice(0, 30));
  const climbBoys = biggestClimb(data.boys.slice(0, 30));
  const topTen = (entries: RankedName[]) => list(entries.slice(0, 10).map((entry) => entry.name));
  return (
    <NamesLayout>
      <Breadcrumbs
        items={[
          { label: "Prénoms", to: "/prenoms" },
          { label: "Par année", to: "/prenoms/annee" },
          { label: String(data.year) },
        ]}
      />
      <header className="space-y-6">
        <PrenomsNav
          current="/prenoms/annee"
          share={
            <ShareButton
              path={namePath.year(data.year)}
              title={`Les prénoms les plus donnés en ${data.year}`}
              text={`Le top des prénoms ${data.year} sur Listou :`}
              card={(format) => nameCards.year(data.year, format)}
              slug={`prenoms-${data.year}`}
            />
          }
        />
        <h1 className="max-w-4xl text-4xl sm:text-6xl">
          Les prénoms les plus donnés en {data.year}
        </h1>
        <p className="max-w-3xl text-lg text-muted">
          En {data.year}, {girl?.name} est le prénom féminin le plus donné en France
          {girl ? ` (${number(girl.count)} naissances)` : ""}, devant {data.girls[1]?.name} et{" "}
          {data.girls[2]?.name}. Chez les garçons, {boy?.name} arrive en tête
          {boy ? ` (${number(boy.count)})` : ""}, devant {data.boys[1]?.name} et{" "}
          {data.boys[2]?.name}.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <LeaderCard entry={girl} sex="filles" color={GIRLS} year={data.year} />
          <LeaderCard entry={boy} sex="garçons" color={BOYS} year={data.year} />
        </div>
        <YearStepper year={data.year} />
      </header>

      <section className={`${card} space-y-4`}>
        <h2 className="text-3xl font-bold">Le classement {data.year}, filles et garçons</h2>
        <p className="text-muted">
          Les 10 premiers prénoms de fille couvrent{" "}
          {percent(topShare(data.girls, data.births.girls), 1)} des naissances de filles publiées,
          les 10 premiers prénoms de garçon {percent(topShare(data.boys, data.births.boys), 1)} de
          celles des garçons.
          {climbGirls && climbGirls.gain > 0
            ? ` Chez les filles, ${climbGirls.entry.name} fait la plus belle progression du top 30 (+${climbGirls.gain} places).`
            : ""}
          {climbBoys && climbBoys.gain > 0
            ? ` Chez les garçons, c’est ${climbBoys.entry.name} (+${climbBoys.gain} places).`
            : ""}
        </p>
        <RankingColumns
          girls={data.girls}
          boys={data.boys}
          compared={data.year > NAME_FIRST_YEAR}
        />
      </section>

      {(data.rising.length > 0 || data.newcomers.length > 0) && (
        <section className={`${card} space-y-6`}>
          <h2 className="text-3xl font-bold">Les révélations de {data.year}</h2>
          {data.rising.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-xl font-bold">Les plus fortes hausses</h3>
              <p className="text-muted">
                Les prénoms qui gagnent le plus de naissances par rapport à {data.year - 1}.
              </p>
              <ul className="flex flex-wrap gap-3">
                {data.rising.map((entry) => (
                  <li key={entry.slug}>
                    <NameLink
                      slug={entry.slug}
                      className="block rounded-2xl border border-line px-4 py-3 hover:border-ink"
                    >
                      <span className="block font-display text-lg font-bold">{entry.name}</span>
                      <span className="block text-sm font-bold text-fir">
                        +{number(entry.count - entry.previousCount)}
                      </span>
                      <span className="block text-xs text-muted">
                        {number(entry.previousCount)} puis {number(entry.count)}
                      </span>
                    </NameLink>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data.newcomers.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-xl font-bold">Les nouveaux prénoms</h3>
              <p className="text-muted">
                Absents du fichier avant {data.year}, donnés au moins 30 fois cette année-là.
              </p>
              <ul className="flex flex-wrap gap-3">
                {data.newcomers.map((entry) => (
                  <li key={entry.slug}>
                    <NameLink
                      slug={entry.slug}
                      className="block rounded-2xl border border-line px-4 py-3 hover:border-ink"
                    >
                      <span className="block font-display text-lg font-bold">{entry.name}</span>
                      <span className="block text-xs text-muted">
                        {number(entry.count)} naissances
                      </span>
                    </NameLink>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      <section className={`${card} space-y-5`}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="text-3xl font-bold">Le prénom n° 1 dans chaque département</h2>
            <p className="mt-2 text-muted">
              Cliquez sur un département pour voir son classement {data.year} complet.
            </p>
          </div>
          <div className="flex gap-2" role="group" aria-label="Sexe">
            {(
              [
                ["girls", "Filles"],
                ["boys", "Garçons"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={sex === value}
                onClick={() => setSex(value)}
                className={`rounded-full border px-4 py-2 text-sm font-bold ${sex === value ? "border-ink bg-ink text-white" : "border-line"}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid items-center gap-8 md:grid-cols-2">
          <FranceMap
            label={`Prénom n° 1 par département en ${data.year}`}
            fill={(code) => {
              const leader = byCode.get(code);
              return leader ? (colors.get(leader.slug) ?? OTHER_COLOR) : GRID;
            }}
            describe={(code) => {
              const leader = byCode.get(code);
              return `${DEPARTMENTS_BY_CODE.get(code)?.name ?? code} : ${leader ? `${leader.name} (${number(leader.count)})` : "aucun effectif publié"}`;
            }}
            href={(code) => ({
              to: namePath.yearDepartment(data.year, DEPARTMENTS_BY_CODE.get(code)?.slug ?? ""),
            })}
          />
          <LeaderLegend counts={counts} colors={colors} />
        </div>
      </section>

      <section className={`${card} space-y-3`}>
        <h2 className="text-2xl font-bold">À retenir sur {data.year}</h2>
        <p>
          Le fichier de l’Insee recense {number(births)} naissances avec un prénom publié en{" "}
          {data.year} : {number(data.births.girls)} filles et {number(data.births.boys)} garçons. Le
          haut du classement féminin se compose de {topTen(data.girls)}. Chez les garçons :{" "}
          {topTen(data.boys)}.
        </p>
        <p>
          Pour comparer, consultez{" "}
          {data.year > NAME_FIRST_YEAR && <YearLink year={data.year - 1} className="underline" />}
          {data.year > NAME_FIRST_YEAR && data.year < NAME_LAST_YEAR && " ou "}
          {data.year < NAME_LAST_YEAR && <YearLink year={data.year + 1} className="underline" />},
          ou ouvrez la fiche d’un prénom pour suivre sa courbe depuis 1900.
        </p>
      </section>

      <DepartmentGrid
        title={`Les prénoms ${data.year} dans votre département`}
        link={(slug, name) => (
          <YearDepartmentLink year={data.year} slug={slug} className="underline">
            {name}
          </YearDepartmentLink>
        )}
      />
      <DecadeGrid
        title="Les prénoms les plus donnés, année par année"
        current={data.year}
        link={(year) => <YearLink year={year} />}
      />
      <Faq items={faqFor(data)} />
      <Source />
    </NamesLayout>
  );
}

export const yearsIndexHead = () => ({
  title: "Prénoms les plus donnés depuis 1900 : le top de chaque année · Listou",
  image: nameCards.page("years", "og"),
  description: `Quel prénom était n° 1 l’année de votre naissance ? Le classement des prénoms de fille et de garçon de ${NAME_FIRST_YEAR} à ${NAME_LAST_YEAR}, année par année, d’après l’Insee.`,
  jsonLd: graph(
    breadcrumbJsonLd(
      [{ label: "Prénoms", to: "/prenoms" }, { label: "Par année" }],
      "/prenoms/annee",
    ),
    datasetJsonLd({
      name: "Prénoms les plus donnés en France depuis 1900",
      description:
        "Prénom n° 1 chez les filles et chez les garçons pour chaque année de naissance.",
      coverage: `${NAME_FIRST_YEAR}/${NAME_LAST_YEAR}`,
    }),
  ),
});

export function YearsIndexPage({ years }: { years: YearsOverview }) {
  const girlReigns = reignsOf(years.map((entry) => ({ year: entry.year, leader: entry.girl })));
  const boyReigns = reignsOf(years.map((entry) => ({ year: entry.year, leader: entry.boy })));
  const latest = years.at(-1);
  const longestGirl = longest(girlReigns);
  const longestBoy = longest(boyReigns);
  return (
    <NamesLayout>
      <Breadcrumbs items={[{ label: "Prénoms", to: "/prenoms" }, { label: "Par année" }]} />
      <header className="space-y-6">
        <PrenomsNav
          current="/prenoms/annee"
          share={
            <ShareButton
              path="/prenoms/annee"
              title="Le prénom n° 1, année après année"
              text="Quel prénom était n° 1 l’année de votre naissance ? Sur Listou :"
              card={(format) => nameCards.page("years", format)}
              slug="prenoms-par-annee"
            />
          }
        />
        <h1 className="max-w-4xl text-4xl sm:text-6xl">Le prénom n° 1, année après année</h1>
        <p className="max-w-3xl text-lg text-muted">
          De {NAME_FIRST_YEAR} à {NAME_LAST_YEAR}, quel prénom a dominé chaque année ?{" "}
          {longestGirl && longestBoy
            ? `Le plus long règne chez les filles est celui de ${longestGirl.name} (${longestGirl.from}–${longestGirl.to}, ${longestGirl.to - longestGirl.from + 1} ans), chez les garçons celui de ${longestBoy.name} (${longestBoy.from}–${longestBoy.to}, ${longestBoy.to - longestBoy.from + 1} ans). `
            : ""}
          Choisissez une année pour lire son classement complet et la carte des prénoms par
          département.
          {latest?.girl && latest.boy
            ? ` En ${latest.year}, ce sont ${latest.girl.name} et ${latest.boy.name}.`
            : ""}
        </p>
      </header>
      <section className={`${card} space-y-8`}>
        <div className="space-y-3">
          <h2 className="flex items-center gap-2 text-2xl font-bold">
            <span className="inline-block h-3 w-3 rounded-full" style={{ background: GIRLS }} />
            Les n° 1 chez les filles
          </h2>
          <Reigns reigns={girlReigns} label="Prénoms féminins n° 1" />
        </div>
        <div className="space-y-3">
          <h2 className="flex items-center gap-2 text-2xl font-bold">
            <span className="inline-block h-3 w-3 rounded-full" style={{ background: BOYS }} />
            Les n° 1 chez les garçons
          </h2>
          <Reigns reigns={boyReigns} label="Prénoms masculins n° 1" />
        </div>
      </section>
      <section className={`${card} space-y-4`}>
        <h2 className="text-2xl font-bold">Les naissances publiées, année par année</h2>
        <p className="text-muted">
          Le creux de 1915–1918 et le baby-boom de 1946 se lisent directement dans les effectifs du
          fichier.
        </p>
        <Legend
          items={[
            { label: "Filles", color: GIRLS },
            { label: "Garçons", color: BOYS },
          ]}
        />
        <TimeChart
          label="Naissances publiées par année, filles et garçons"
          series={[
            { label: "Filles", color: GIRLS, values: years.map((entry) => entry.births.girls) },
            { label: "Garçons", color: BOYS, values: years.map((entry) => entry.births.boys) },
          ]}
          unit="naissances"
        />
      </section>
      <DecadeGrid title="Choisir une année" link={(year) => <YearLink year={year} />} />
      <section className={`${card} space-y-3`}>
        <h2 className="text-2xl font-bold">Tableau des n° 1</h2>
        <div className="max-h-[36rem] overflow-auto">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-surface">
              <tr>
                <th scope="col" className="py-2">
                  Année
                </th>
                <th scope="col">Filles</th>
                <th scope="col">Garçons</th>
                <th scope="col" className="text-right">
                  Naissances publiées
                </th>
              </tr>
            </thead>
            <tbody>
              {years.toReversed().map((entry) => (
                <tr key={entry.year} className="border-t border-line">
                  <th scope="row" className="py-2">
                    <YearLink year={entry.year} className="underline" />
                  </th>
                  <td>
                    {entry.girl ? (
                      <NameLink slug={entry.girl.slug}>{entry.girl.name}</NameLink>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    {entry.boy ? <NameLink slug={entry.boy.slug}>{entry.boy.name}</NameLink> : "—"}
                  </td>
                  <td className="text-right tabular-nums">{number(totalBirths(entry))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <Source />
    </NamesLayout>
  );
}

const yearDepartmentFaq = (data: YearDepartmentData) => {
  const girl = data.girls[0];
  const boy = data.boys[0];
  const place = `${data.department.name} (${data.department.code})`;
  return [
    {
      question: `Quel est le prénom le plus donné en ${data.year} en ${data.department.name} ?`,
      answer: `${girl?.name ?? "Aucun prénom"} chez les filles et ${boy?.name ?? "aucun prénom"} chez les garçons, parmi les naissances de ${place} recensées par l’Insee.`,
    },
    {
      question: `Les prénoms populaires de ${data.department.name} sont-ils ceux de toute la France ?`,
      answer: `Souvent en partie : ${list([girl, boy].filter((e): e is RankedName => !!e).map((entry) => `${entry.name} est ${entry.nationalRank ? `${ordinal(entry.nationalRank)} en France` : "hors du top 100 national"}`))}.`,
    },
  ];
};

export const yearDepartmentHead = (data: YearDepartmentData) => {
  const girl = data.girls[0];
  const boy = data.boys[0];
  const place = `${data.department.name} (${data.department.code})`;
  return {
    title: `Prénoms ${data.year} – ${place} : top filles et garçons · Listou`,
    image: nameCards.yearDepartment(data.year, data.department.slug, "og"),
    // Never indexed (see NAME_INDEX_MIN_TOTAL): the year and department pages cover them.
    noindex: true,
    description: `Les prénoms les plus donnés en ${data.year} en ${data.department.name} : ${girl?.name ?? "—"} chez les filles, ${boy?.name ?? "—"} chez les garçons. Classement comparé à la France, d’après l’Insee.`,
    jsonLd: graph(
      breadcrumbJsonLd(
        [
          { label: "Prénoms", to: "/prenoms" },
          { label: "Par année", to: "/prenoms/annee" },
          { label: String(data.year), to: namePath.year(data.year) },
          { label: data.department.name },
        ],
        namePath.yearDepartment(data.year, data.department.slug),
      ),
      rankingJsonLd(
        `Prénoms de fille les plus donnés en ${data.year}, ${place}`,
        data.girls.slice(0, 20),
      ),
      rankingJsonLd(
        `Prénoms de garçon les plus donnés en ${data.year}, ${place}`,
        data.boys.slice(0, 20),
      ),
      faqJsonLd(yearDepartmentFaq(data)),
      datasetJsonLd({
        name: `Prénoms donnés en ${data.year} en ${data.department.name}`,
        description: `Classement des prénoms des enfants nés en ${place} en ${data.year}.`,
        coverage: String(data.year),
        spatial: place,
      }),
    ),
  };
};

export function YearDepartmentPage({ data }: { data: YearDepartmentData }) {
  const { year, department } = data;
  const girl = data.girls[0];
  const boy = data.boys[0];
  const { previous, next } = yearsAround(year);
  return (
    <NamesLayout>
      <Breadcrumbs
        items={[
          { label: "Prénoms", to: "/prenoms" },
          { label: "Par année", to: "/prenoms/annee" },
          { label: String(year), to: namePath.year(year) },
          { label: department.name },
        ]}
      />
      <header className="space-y-6">
        <PrenomsNav
          current="/prenoms/annee"
          share={
            <ShareButton
              path={namePath.yearDepartment(year, department.slug)}
              title={`Les prénoms ${year} en ${department.name}`}
              text={`Le top des prénoms ${year} en ${department.name} sur Listou :`}
              card={(format) => nameCards.yearDepartment(year, department.slug, format)}
              slug={`prenoms-${year}-${department.slug}`}
            />
          }
        />
        <h1 className="max-w-4xl text-4xl sm:text-6xl">
          Prénoms les plus donnés en {year} : {department.name} ({department.code})
        </h1>
        <p className="max-w-3xl text-lg text-muted">
          {girl && boy
            ? `En ${year}, ${girl.name} est le prénom féminin le plus donné en ${department.name} (${number(girl.count)} naissances${nationalNote(girl)}). Chez les garçons, ${boy.name} arrive en tête (${number(boy.count)}${nationalNote(boy)}).`
            : `Le fichier de l’Insee ne publie pas de classement pour ${department.name} en ${year}.`}
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <LeaderCardLocal entry={girl} sex="filles" color={GIRLS} department={department.name} />
          <LeaderCardLocal entry={boy} sex="garçons" color={BOYS} department={department.name} />
        </div>
        <div className="flex flex-wrap gap-2">
          {previous && (
            <YearDepartmentLink year={previous} slug={department.slug} className={pill}>
              <ChevronLeft size={16} aria-hidden="true" />
              {previous}
            </YearDepartmentLink>
          )}
          {next && (
            <YearDepartmentLink year={next} slug={department.slug} className={pill}>
              {next}
              <ChevronRight size={16} aria-hidden="true" />
            </YearDepartmentLink>
          )}
          <YearLink year={year} className={pill}>
            Toute la France en {year}
          </YearLink>
          <DepartmentLink slug={department.slug} className={pill}>
            {department.name} depuis 1900
          </DepartmentLink>
        </div>
      </header>
      <section className={`${card} space-y-4`}>
        <h2 className="text-3xl font-bold">
          Le top {data.girls.length} {year} en {department.name}
        </h2>
        <p className="text-muted">
          {number(data.births.girls + data.births.boys)} naissances avec un prénom publié :{" "}
          {number(data.births.girls)} filles et {number(data.births.boys)} garçons. Le rang national
          figure sous chaque prénom lorsqu’il est dans le top 100.
        </p>
        <RankingColumns girls={data.girls} boys={data.boys} initial={30} />
      </section>
      <DepartmentGrid
        title={`${year} dans les autres départements`}
        current={department.code}
        link={(slug, name) => (
          <YearDepartmentLink year={year} slug={slug} className="underline">
            {name}
          </YearDepartmentLink>
        )}
      />
      <Faq items={yearDepartmentFaq(data)} />
      <Source />
    </NamesLayout>
  );
}

function LeaderCardLocal({
  entry,
  sex,
  color,
  department,
}: {
  entry: RankedName | undefined;
  sex: string;
  color: string;
  department: string;
}) {
  if (!entry) return null;
  return (
    <div className="rounded-3xl p-6 text-white" style={{ background: color }}>
      <p className="text-sm opacity-90">
        N° 1 chez les {sex} en {department}
      </p>
      <NameLink slug={entry.slug} className="mt-1 block font-display text-5xl font-bold">
        {entry.name}
      </NameLink>
      <p className="mt-2 opacity-90">{number(entry.count)} naissances</p>
    </div>
  );
}
